import { formatCopy } from "../../theme/copy";
import { useRef, useState, type KeyboardEvent } from "react";
import { Box } from "@mui/material";
import { highlighterDiv } from "./styles";
import { useThemeContext } from "../../globalContext/global-context";
import type { ThemeV2 } from "../../util/types";
import type { QuestionViewProps } from "../contracts";
import type {
  TextHighlighterDefinition,
  TextHighlighterDraft,
  TextHighlighterEvent,
} from "./core";

type ViewProps = QuestionViewProps<
  TextHighlighterDefinition,
  TextHighlighterDraft,
  TextHighlighterEvent
>;

type Word = { start: number; end: number; text: string };
const wordsIn = (text: string): Word[] =>
  [...text.matchAll(/\S+/g)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
    text: match[0],
  }));

const nextWord = (
  key: string,
  current: number,
  last: number,
): number | null => {
  if (key === "ArrowRight" || key === "ArrowDown")
    return Math.min(current + 1, last);
  if (key === "ArrowLeft" || key === "ArrowUp") return Math.max(current - 1, 0);
  if (key === "Home") return 0;
  if (key === "End") return last;
  return null;
};

const segmentsFor = (
  text: string,
  words: Word[],
  ranges: TextHighlighterDraft["ranges"],
  activeWord?: Word,
) => {
  const boundaries = new Set([0, text.length]);
  for (const range of [...words, ...ranges]) {
    boundaries.add(range.start);
    boundaries.add(range.end);
  }
  const positions = [...boundaries].sort((a, b) => a - b);
  const isActive = (start: number, end: number): boolean =>
    activeWord !== undefined &&
    activeWord.start <= start &&
    activeWord.end >= end;
  return positions.slice(0, -1).map((start, index) => ({
    start,
    end: positions[index + 1],
    highlighted: ranges.some(
      (range) => range.start <= start && range.end >= positions[index + 1],
    ),
    active: isActive(start, positions[index + 1]),
  }));
};

const selectedTextRange = (editor: HTMLDivElement) => {
  const selection = globalThis.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (
    range.collapsed ||
    !editor.contains(range.startContainer) ||
    !editor.contains(range.endContainer)
  )
    return null;
  const prefix = document.createRange();
  prefix.selectNodeContents(editor);
  prefix.setEnd(range.startContainer, range.startOffset);
  const start = prefix.toString().length;
  const end = start + range.toString().length;
  selection.removeAllRanges();
  return { start, end };
};

const HighlightSegments = ({
  definition,
  segments,
  keyboardFocused,
  theme,
}: {
  definition: TextHighlighterDefinition;
  segments: ReturnType<typeof segmentsFor>;
  keyboardFocused: boolean;
  theme: ThemeV2;
}) =>
  segments.map((segment) => (
    <Box
      component="span"
      key={`${segment.start}-${segment.end}`}
      className={segment.highlighted ? "highlight" : undefined}
      sx={{
        backgroundColor: segment.highlighted ? definition.color : undefined,
        outline:
          keyboardFocused && segment.active
            ? `1px dashed ${theme.tokens.color.action.primary}`
            : undefined,
        outlineOffset: keyboardFocused && segment.active ? 1 : undefined,
      }}
    >
      {definition.text.slice(segment.start, segment.end)}
    </Box>
  ));

const KeyboardHelp = ({
  theme,
  status,
}: {
  theme: ThemeV2;
  status: string;
}) => (
  <>
    <Box
      id="text-highlighter-keyboard-help"
      sx={{ marginTop: `${theme.tokens.spacing["2"]}px` }}
    >
      {theme.copy.status.highlighterHelp}
    </Box>
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "absolute",
        width: 1,
        height: 1,
        overflow: "hidden",
        clipPath: "inset(50%)",
      }}
    >
      {status}
    </Box>
  </>
);

export const TextHighlighterView = (props: ViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const editorRef = useRef<HTMLDivElement>(null);
  const { definition, draft } = props;
  const [wordIndex, setWordIndex] = useState(0);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const words = wordsIn(definition.text);
  const activeWord = words[Math.min(wordIndex, words.length - 1)];
  const segments = segmentsFor(
    definition.text,
    words,
    draft.ranges,
    activeWord,
  );

  const handleSelection = () => {
    if (!props.controls.canSubmit || !editorRef.current) return;
    const range = selectedTextRange(editorRef.current);
    if (range) props.dispatch({ type: "toggle-range", ...range });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!props.controls.canSubmit || !words.length) return;
    if (event.key === " " && activeWord) {
      event.preventDefault();
      props.dispatch({
        type: "toggle-range",
        start: activeWord.start,
        end: activeWord.end,
      });
      return;
    }
    const next = nextWord(event.key, wordIndex, words.length - 1);
    if (next === null) return;
    event.preventDefault();
    setWordIndex(next);
  };

  return (
    <Box
      data-testid="text-highlighter-view"
      sx={{
        width: "100%",
        padding: `${theme.tokens.spacing["4"]}px`,
        backgroundColor: theme.tokens.color.surface.card,
        border: `1px solid ${theme.tokens.color.action.primary}`,
        borderRadius: `${theme.tokens.shape.radius.lg}px`,
        boxShadow: theme.tokens.elevation["2"],
      }}
    >
      <Box
        ref={editorRef}
        role="group"
        tabIndex={props.controls.canSubmit && words.length ? 0 : -1}
        aria-label={theme.copy.labels.textToHighlight}
        aria-describedby="text-highlighter-keyboard-help"
        onFocus={() => setKeyboardFocused(true)}
        onBlur={() => setKeyboardFocused(false)}
        onMouseUp={handleSelection}
        onKeyDown={handleKeyDown}
        data-testid="text-highlighter-selector-div"
        sx={[
          highlighterDiv(theme),
          {
            userSelect: "text",
          },
        ]}
      >
        <HighlightSegments
          definition={definition}
          segments={segments}
          keyboardFocused={keyboardFocused}
          theme={theme}
        />
      </Box>
      <KeyboardHelp
        theme={theme}
        status={
          keyboardFocused && activeWord
            ? formatCopy(theme.copy.status.highlightWord, {
                word: activeWord.text,
                index: wordIndex + 1,
                total: words.length,
                state: draft.ranges.some(
                  (range) =>
                    range.start <= activeWord.start &&
                    range.end >= activeWord.end,
                )
                  ? theme.copy.status.highlighted
                  : theme.copy.status.notHighlighted,
              })
            : ""
        }
      />
    </Box>
  );
};
